import uniqueId from 'lodash/uniqueId';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { SecondarySolidButton, SecondaryTextButton } from '@bibliocommons/base-buttons';
import { SecondaryLink } from '@bibliocommons/base-links';
import TextField from '@bibliocommons/text-field';

import { Table, TableHeader, TableHeading, TableBody, TableRow, TableCell } from 'app/components/shared/Table';
import ContextMenu from 'app/components/admin/browse/shared/ContextMenu';
import ReOrderButtons from 'app/components/admin/browse/shared/ReOrderButtons';
import { DEFAULT_LINK_VALIDATION, NUM_MAX_LINKS } from 'app/constants/admin/browse/home';
import { validateLink } from 'app/helpers/admin/browse/home';
import { linksShape, linkValidationShape } from 'app/shapes/admin/browse/home';
import TableWrapper from './TableWrapper';

import './EditorLinks.scss';

const ENTER_KEY_CODE = 13;

const EditorLinks = ({ links, onChange, validation }) => {
  const createLinkLabelRef = useRef(null);

  const [state, setState] = useState({
    createLink: {
      label: '',
      url: ''
    },
    createLinkValidation: DEFAULT_LINK_VALIDATION,
    editLink: null,
    editLinkValidation: DEFAULT_LINK_VALIDATION,
    indexOfLinkToEdit: -1
  });

  useEffect(() => {
    const { createLink: newCreateLinkValidation } = validation;

    if (!newCreateLinkValidation.isValid) {
      setState(prevState => {
        return {
          ...prevState,
          createLinkValidation: newCreateLinkValidation
        };
      });
    }
  }, [validation]);

  const handleCreateLinkLabelChange = newLabel => {
    setState(prevState => {
      return {
        ...prevState,
        createLink: {
          ...prevState.createLink,
          label: newLabel
        }
      };
    });
  };

  const handleCreateLinkSave = () => {
    const { createLink } = state;

    const newCreateLinkValidation = validateLink(createLink);

    if (newCreateLinkValidation.isValid) {
      setState(prevState => {
        return {
          ...prevState,
          createLink: {
            label: '',
            url: ''
          },
          createLinkValidation: newCreateLinkValidation
        };
      });

      if (createLinkLabelRef.current) {
        createLinkLabelRef.current.focus();
      }

      onChange(links.concat(createLink));
    } else {
      setState(prevState => {
        return {
          ...prevState,
          createLinkValidation: newCreateLinkValidation
        };
      });
    }
  };

  const handleCreateLinkInputKeyPress = event => {
    const keyCode = event.keyCode || event.charCode;
    if (keyCode !== ENTER_KEY_CODE) {
      return;
    }

    event.preventDefault();
    handleCreateLinkSave();
  };

  const handleCreateLinkUrlChange = newUrl => {
    setState(prevState => {
      return {
        ...prevState,
        createLink: {
          ...prevState.createLink,
          url: newUrl
        }
      };
    });
  };

  const handleDeleteClick = linkIndex => {
    const newLinks = [...links];

    newLinks.splice(linkIndex, 1);

    onChange(newLinks);
  };

  const handleEditClick = linkIndex => {
    const linkToEdit = links[linkIndex];

    setState(prevState => {
      return {
        ...prevState,
        indexOfLinkToEdit: linkIndex,
        editLink: {
          label: linkToEdit.label,
          url: linkToEdit.url
        }
      };
    });
  };

  const handleEditLinkLabelChange = newLabel => {
    setState(prevState => {
      return {
        ...prevState,
        editLink: {
          ...prevState.editLink,
          label: newLabel
        }
      };
    });
  };

  const handleEditLinkCancel = () => {
    setState(prevState => {
      return {
        ...prevState,
        indexOfLinkToEdit: -1,
        editLink: null,
        editLinkValidation: DEFAULT_LINK_VALIDATION
      };
    });
  };

  const handleEditLinkSave = () => {
    const { indexOfLinkToEdit, editLink } = state;

    const newEditLinkValidation = validateLink(editLink);

    if (newEditLinkValidation.isValid) {
      const linkToEdit = links[indexOfLinkToEdit];

      linkToEdit.label = editLink.label;
      linkToEdit.url = editLink.url;

      setState(prevState => {
        return {
          ...prevState,
          indexOfLinkToEdit: -1,
          editLink: null,
          editLinkValidation: DEFAULT_LINK_VALIDATION
        };
      });

      onChange(links);
    } else {
      setState(prevState => {
        return {
          ...prevState,
          editLinkValidation: newEditLinkValidation
        };
      });
    }
  };

  const handleEditLinkInputKeyPress = event => {
    const keyCode = event.keyCode || event.charCode;
    if (keyCode !== ENTER_KEY_CODE) {
      return;
    }

    handleEditLinkSave();
  };

  const handleEditLinkUrlChange = newUrl => {
    setState(prevState => {
      return {
        ...prevState,
        editLink: {
          ...prevState.editLink,
          url: newUrl
        }
      };
    });
  };

  const handleLinksReOrder = newLinks => {
    onChange(newLinks);
  };

  const hasCreateLinkFields = links.length < NUM_MAX_LINKS;
  const uniqueInstanceKey = useMemo(() => {
    return uniqueId('EditorLinks_');
  }, []);
  const keyPrefix = `EditorLinks_${uniqueInstanceKey}`;
  const createLinkRowKey = `${keyPrefix}_CreateLinkRow`;
  const createLinkLabelInputId = `${keyPrefix}_CreateLinkLabel`;
  const createLinkUrlInputId = `${keyPrefix}_CreateLinkUrl`;

  const { createLink, createLinkValidation, editLink, editLinkValidation, indexOfLinkToEdit } = state;
  const { label: errorMessageCreateLinkLabel, url: errorMessageCreateLinkUrl } = createLinkValidation.errorByAttribute;

  return (
    <div className="cp-admin-browse-home-editor-links">
      <TableWrapper>
        <Table>
          <TableHeader>
            <TableHeading>Label</TableHeading>
            <TableHeading>URL</TableHeading>
            <TableHeading />
          </TableHeader>
          <TableBody>
            {links.map((link, index) => {
              const { label, url } = link;
              const { label: labelError, url: urlError } = editLinkValidation.errorByAttribute;

              const rowKey = `${keyPrefix}_LinksRow_${index}`;
              const cancelEditButtonKey = `${rowKey}_CancelEdit`;
              const saveButtonKey = `${rowKey}_Save}`;
              const labelInputId = `${rowKey}_Label`;
              const urlInputId = `${rowKey}_Url`;
              const urlLinkKey = `${rowKey}_UrlLink`;

              const isEditing = index === indexOfLinkToEdit;

              return (
                <TableRow key={rowKey}>
                  <TableCell className="cell-editable-link">
                    {isEditing ? (
                      <TextField
                        errorMessage={labelError}
                        id={labelInputId}
                        label="Label"
                        onChange={handleEditLinkLabelChange}
                        onKeyPress={handleEditLinkInputKeyPress}
                        type="text"
                        value={editLink.label}
                      />
                    ) : (
                      <span>{label}</span>
                    )}
                  </TableCell>
                  <TableCell className="cell-editable-link" colSpan={isEditing ? 2 : 1}>
                    {isEditing ? (
                      <div className="edit-url-wrapper">
                        <TextField
                          className="input-url"
                          errorMessage={urlError}
                          id={urlInputId}
                          label="Url"
                          onChange={handleEditLinkUrlChange}
                          onKeyPress={handleEditLinkInputKeyPress}
                          type="text"
                          value={editLink.url}
                        />
                        <SecondarySolidButton
                          className="action-edit-link"
                          dataKey={saveButtonKey}
                          handleClick={handleEditLinkSave}
                          type="button"
                        >
                          Save
                        </SecondarySolidButton>
                        <SecondaryTextButton
                          className="action-edit-link"
                          dataKey={cancelEditButtonKey}
                          handleClick={handleEditLinkCancel}
                          type="button"
                        >
                          Cancel
                        </SecondaryTextButton>
                      </div>
                    ) : (
                      <SecondaryLink dataKey={urlLinkKey} href={url} target="_blank">
                        {url}
                      </SecondaryLink>
                    )}
                  </TableCell>
                  {!isEditing && (
                    <TableCell className="cell-actions">
                      <div className="row-actions">
                        <ReOrderButtons index={index} items={links} onChange={handleLinksReOrder} />
                        <ContextMenu
                          className="action-context-menu"
                          confirmDeleteModalHeading="Delete link"
                          confirmDeleteModalMessage={`Are you sure you want to delete the ${label} link?`}
                          onDeleteClick={() => handleDeleteClick(index)}
                          onEditClick={() => handleEditClick(index)}
                        />
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}

            {hasCreateLinkFields && (
              <TableRow key={createLinkRowKey}>
                <TableCell>
                  <TextField
                    errorMessage={errorMessageCreateLinkLabel}
                    id={createLinkLabelInputId}
                    label="Label"
                    onChange={handleCreateLinkLabelChange}
                    onKeyPress={handleCreateLinkInputKeyPress}
                    ref={createLinkLabelRef}
                    type="text"
                    value={createLink.label}
                  />
                </TableCell>
                <TableCell colSpan={2}>
                  <div className="edit-url-wrapper">
                    <TextField
                      className="input-url"
                      errorMessage={errorMessageCreateLinkUrl}
                      id={createLinkUrlInputId}
                      label="URL"
                      onChange={handleCreateLinkUrlChange}
                      onKeyPress={handleCreateLinkInputKeyPress}
                      type="text"
                      value={createLink.url}
                    />

                    <SecondarySolidButton
                      className="action-edit-link"
                      dataKey="create-link-button"
                      handleClick={handleCreateLinkSave}
                      type="button"
                    >
                      Add
                    </SecondarySolidButton>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>
    </div>
  );
};

EditorLinks.defaultProps = {
  validation: {
    createLink: DEFAULT_LINK_VALIDATION
  }
};

EditorLinks.propTypes = {
  links: linksShape.isRequired,
  onChange: PropTypes.func.isRequired,
  validation: PropTypes.shape({
    createLink: linkValidationShape.isRequired
  })
};

export default EditorLinks;
